/**
 * Runtime chamber theming (spec §5/§16, D-008).
 *
 * Applying a chamber's brand means overriding exactly six custom properties. Not a
 * stylesheet swap, not a build per chamber — six properties, so that the blast radius of
 * a badly-chosen brand colour is bounded and inspectable.
 *
 * If the chamber's colour cannot carry legible text, we keep their colour for accents and
 * fall back to Provenir ink for anything that hosts text, and say so. A chamber whose
 * brand is mid-yellow should not end up with invisible buttons.
 */
import { contrastRatio, deriveAccent, deriveBrandRamp, WCAG_AA_NORMAL } from './color.js';

export interface ChamberTheme {
  primaryColor: string;
  secondaryColor: string;
}

export interface AppliedTheme {
  applied: boolean;
  degraded: boolean;
  contrast: number;
  reason?: string;
}

const PROVENIR_INK = '#14301E';

export function applyChamberTheme(
  theme: ChamberTheme | null,
  root: HTMLElement | null = typeof document === 'undefined' ? null : document.documentElement,
): AppliedTheme {
  if (!root) return { applied: false, degraded: false, contrast: 0, reason: 'no document' };

  if (!theme) {
    clearChamberTheme(root);
    return { applied: false, degraded: false, contrast: 0 };
  }

  const ramp = deriveBrandRamp(theme.primaryColor);
  if (!ramp) {
    clearChamberTheme(root);
    return { applied: false, degraded: true, contrast: 0, reason: 'primary colour is not a hex value' };
  }

  const accent = deriveAccent(theme.secondaryColor);

  // A brand colour that fails AA still themes the soft tints, hairlines and accents —
  // those carry no text. Only the two surfaces that host text fall back.
  const brandSurface = ramp.degraded ? PROVENIR_INK : ramp.brand;
  const brandHover = ramp.degraded ? '#0C1F13' : ramp.brandHover;
  const brandOn = ramp.degraded ? '#F4F5EC' : ramp.brandOn;

  root.style.setProperty('--pv-brand', brandSurface);
  root.style.setProperty('--pv-brand-hover', brandHover);
  root.style.setProperty('--pv-brand-soft', ramp.brandSoft);
  root.style.setProperty('--pv-brand-line', ramp.brandLine);
  root.style.setProperty('--pv-brand-on', brandOn);
  if (accent) {
    root.style.setProperty('--pv-accent', accent.accent);
    root.style.setProperty('--pv-accent-on', accent.accentOn);
  }
  root.setAttribute('data-pv-themed', 'true');

  return {
    applied: true,
    degraded: ramp.degraded,
    contrast: Number(ramp.contrast.toFixed(2)),
    reason: ramp.degraded
      ? `That colour reaches only ${ramp.contrast.toFixed(1)}:1 against any text colour — WCAG AA needs ${WCAG_AA_NORMAL}:1. It is being used for accents, and Provenir ink is carrying the buttons.`
      : undefined,
  };
}

export function clearChamberTheme(
  root: HTMLElement | null = typeof document === 'undefined' ? null : document.documentElement,
): void {
  if (!root) return;
  for (const prop of [
    '--pv-brand',
    '--pv-brand-hover',
    '--pv-brand-soft',
    '--pv-brand-line',
    '--pv-brand-on',
    '--pv-accent',
    '--pv-accent-on',
  ]) {
    root.style.removeProperty(prop);
  }
  root.removeAttribute('data-pv-themed');
}

/** Used by the chamber branding editor to warn *before* a colour is saved. */
export function previewBrandContrast(primaryHex: string): {
  ok: boolean;
  contrast: number;
  foreground: string;
  message: string;
} {
  const ramp = deriveBrandRamp(primaryHex);
  if (!ramp) {
    return { ok: false, contrast: 0, foreground: '#F4F5EC', message: 'Enter a 6-digit hex colour.' };
  }
  const ratio = contrastRatio(ramp.brand, ramp.brandOn);
  return {
    ok: !ramp.degraded,
    contrast: Number(ratio.toFixed(2)),
    foreground: ramp.brandOn,
    message: ramp.degraded
      ? `Buttons in this colour reach ${ratio.toFixed(1)}:1 — below the 4.5:1 needed to be readable. Your colour will still be used for accents and highlights.`
      : `Good contrast: ${ratio.toFixed(1)}:1.`,
  };
}
